import { useRef, useState } from 'react';
import { useT } from '@/store';
import { useAllVehicles } from '@/hooks/useFleet';
import {
  useCreateServicePlan, useCreateServiceRecord, useUpdateServicePlan,
  useUpdateServiceRecord,
} from '@/hooks/useServicing';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState } from '@/components/ui/Panel';

/**
 * The form V-11 shipped without.
 *
 * AUDIT.md records the gap under V-11's "Kas dar NEBAIGTA": nine tested HTTP
 * routes, a read-only panel, and a setup-checklist step telling a new carrier
 * "Sudaryti priežiūros planą" — while the only way to create a plan or a
 * service was the API. So the feature was worth nothing to the person it is
 * for, and every install needed a phone call to the vendor, which is exactly
 * the done-for-you cost this audit exists to remove.
 *
 * ONE modal, two modes, because a carrier does both jobs from the same panel
 * and in the same minute: `mode="plan"` writes the RULE (every 60 000 km, or
 * every 12 months) and `mode="record"` writes what was actually DONE. They
 * share the vocabulary the panel already established — a service type is
 * "Darbas", the same word ServicePanel's `servicing.colService` column uses —
 * because two words for one thing on two screens is how a carrier concludes
 * they are two different things.
 *
 * THE RULE THIS FORM EXISTS TO NOT BREAK
 * ---------------------------------------
 * An empty odometer and an empty cost are sent as NULL. Never 0.
 *
 * This is not tidiness. core/servicing.py reports a service with no odometer
 * as `last_service_has_no_odometer` — a visible gap with a five-second fix —
 * whereas a 0 written in its place tells the engine the truck came out of the
 * workshop with a fresh odometer, silently starts the next 60 000 km from
 * zero, and reads as healthy for years. maintenance_spend() reports a cost of
 * None rather than 0.00 for the same reason one level up: "0,00 € beside 3
 * services" reads as warranty work rather than as three invoices nobody typed
 * in. blueprints/servicing.py enforces both on the way in; a form that folded
 * an empty box into a zero would defeat that enforcement before it ran, and
 * nothing anywhere would raise.
 *
 * And the converse, which is just as real: a "0" somebody typed ON PURPOSE is
 * sent as 0. A brand-new truck's odometer and a job done under warranty are
 * both genuine values, and a form that cannot say them makes a carrier invent
 * a fake number instead.
 *
 * WHY VALIDATION HAPPENS HERE AS WELL AS ON THE SERVER
 * ----------------------------------------------------
 * A plan needs interval_km or interval_months; schema.sql has the CHECK and
 * the route answers it with a readable sentence. It is answered here too, and
 * the reason has changed: the server used to reply in English, and now replies
 * with the SAME Lithuanian sentence this form shows (core/i18n.py's
 * err.service_* keys are copied from these `serviceForm.err*` strings, and a
 * test asserts they stay identical). So this is no longer a translation
 * stand-in — it is the round trip saved. The carrier reads the same words
 * either way, which is the point of keeping the two copies in step.
 *
 * The save button is disabled only while a request is in flight, never
 * because a field is empty. A greyed-out button that will not say what is
 * missing is the other way this form could send somebody back to the phone.
 */

/** A value that was typed but cannot be a number. Distinct from null, which
 *  means nobody typed anything — the one distinction this whole file is
 *  about. Uppercase so eslint's varsIgnorePattern leaves it alone. */
const INVALID = Symbol('invalid');

/**
 * '' -> null, '0' -> 0.
 *
 * The two lines that carry the module docstring's rule. An empty box is an
 * absence and stays one; a typed zero is a measurement and survives as one.
 * Anything else is refused rather than quietly coerced: Number('') is 0 and
 * Number('abc') is NaN, and both of those reaching the body would be a
 * silently wrong figure rather than a visible complaint.
 */
function wholeNumber(raw, minimum) {
  const text = String(raw ?? '').trim();
  if (text === '') return null;
  if (!/^\d+$/.test(text)) return INVALID;
  const parsed = Number(text);
  if (!Number.isSafeInteger(parsed) || parsed < minimum) return INVALID;
  return parsed;
}

/** The two fields that name the row rather than describe it. Neither exists
 *  on the stored row under that name — the columns are plan_id and record_id
 *  — so comparing them would make every body look changed, which is exactly
 *  what happened to the record path until this list gained its second entry. */
const ROW_ID_FIELDS = ['planId', 'recordId'];

/**
 * Whether a PATCH body would change anything about the row it names.
 *
 * Compared field by field against the stored row rather than by tracking
 * "dirty" flags on the inputs: a box typed in and typed back out again is not
 * a change, and a flag would call it one.
 */
function unchanged(body, row) {
  return Object.entries(body).every(
    ([field, value]) => ROW_ID_FIELDS.includes(field) || row[field] === value);
}

/**
 * A stored number into a text box. null stays '' — the same absence rule the
 * two parsers above enforce in the other direction, so a plan with no month
 * interval opens with an empty box rather than "null" or "0".
 */
function numberField(value) {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * A workshop invoice, or null. Same rule, plus the Lithuanian decimal comma:
 * a carrier copying "148,50" off a bill types it with the separator his
 * keyboard and his invoice both use, and rejecting that would teach him the
 * cost field is broken.
 */
function money(raw) {
  const text = String(raw ?? '').trim().replace(',', '.');
  if (text === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return INVALID;
  return Number(text);
}

/**
 * Props:
 *   mode         'plan' (default) writes a service_plans rule, 'record'
 *                writes a service_records entry.
 *   vehicleId    optional pre-selection. In record mode it fills the picker;
 *                in plan mode its presence also opens the form on the
 *                per-vehicle scope, since a plan started from one truck's row
 *                is almost never meant to move the whole fleet.
 *   serviceType  optional prefill, e.g. the service_type off a due row.
 *   plan         an existing service_plans row to EDIT rather than create.
 *                Plan mode only. Its presence is what makes this a PATCH, and
 *                it is what the 409 on a duplicate plan has been telling
 *                carriers to do since the route shipped — "redaguokite jį, o
 *                ne kurkite antrą" — while nothing in the console could.
 *   onClose      called on cancel, on the ✕, on Escape, and after a save.
 *   onSaved      optional, called with the created row before onClose.
 */
export function ServiceFormModal({
  mode = 'plan',
  vehicleId: initialVehicleId = '',
  serviceType: initialServiceType = '',
  plan = null,
  // V-60. An existing service record, to be corrected. The route has
  // accepted a PATCH on every field since V-11 and nothing in the console
  // could send one — so a mistyped odometer, the exact thing this form's own
  // hint tells a carrier is "pataisoma per kelias sekundes", had no screen
  // to be corrected on, while it silently moved every future deadline
  // core/servicing.py computes for that truck.
  record = null,
  onClose,
  onSaved,
}) {
  const t = useT();
  const isPlan = mode === 'plan';
  const isEdit = Boolean(isPlan ? plan : record);

  // useAllVehicles, NOT useVehicles: the vehicles endpoint pages at 25 and a
  // picker missing the truck you need looks exactly like the truck having
  // been deleted. See the comment on useAllVehicles() in hooks/useFleet.js —
  // this is the same bug the defect form already shipped once.
  const { data: vehicles = [] } = useAllVehicles();

  // Both hooks run on every render; only one of them is ever fired. Calling
  // them conditionally would break the rules of hooks the moment `mode`
  // changed under a mounted modal.
  const createPlan = useCreateServicePlan();
  const updatePlan = useUpdateServicePlan();
  const createRecord = useCreateServiceRecord();
  const updateRecord = useUpdateServiceRecord();
  const save = isPlan
    ? (isEdit ? updatePlan : createPlan)
    : (isEdit ? updateRecord : createRecord);

  // Editing starts from what is STORED, not from the placeholders. On a new
  // plan the notice boxes are empty and show the defaults in grey, because
  // nothing has been decided yet; on an existing one they show the figures
  // actually in force, since "what is this rule doing right now" is the
  // question somebody opening an edit form is asking.
  const [serviceType, setServiceType] = useState(
    plan?.service_type ?? record?.service_type ?? initialServiceType);
  const [scope, setScope] = useState(
    plan ? (plan.vehicle_id ? 'vehicle' : 'fleet') : (initialVehicleId ? 'vehicle' : 'fleet'));
  const [vehicleId, setVehicleId] = useState(
    plan?.vehicle_id ?? record?.vehicle_id ?? initialVehicleId);
  const [intervalKm, setIntervalKm] = useState(numberField(plan?.interval_km));
  const [intervalMonths, setIntervalMonths] = useState(numberField(plan?.interval_months));
  const [noticeDays, setNoticeDays] = useState(numberField(plan?.notice_days));
  const [noticeKm, setNoticeKm] = useState(numberField(plan?.notice_km));
  // Seeded from the stored record when there is one, for the reason the
  // plan fields above are: somebody opening a correction is asking "what
  // does this row say", and an empty form would make him retype every field
  // to change one.
  const [performedAt, setPerformedAt] = useState(record?.performed_at ?? '');
  const [odometerKm, setOdometerKm] = useState(numberField(record?.odometer_km));
  const [costEur, setCostEur] = useState(numberField(record?.cost_eur));
  const [workshop, setWorkshop] = useState(record?.workshop ?? '');
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [formError, setFormError] = useState(null);

  // Belt and braces against a double save, and they guard different windows.
  // `disabled` covers the second click of an impatient double-click once
  // React has re-rendered; this ref covers the one that lands before it has.
  // A service posted twice is not a cosmetic duplicate: the natural key
  // rejects it with a 409, and on the plan side a fleet that looks serviced
  // twice as often as it is pushes every deadline the engine computes out
  // into the future — the one direction a maintenance module must not drift.
  const inFlight = useRef(false);

  const planBody = () => {
    const km = wholeNumber(intervalKm, 1);
    const months = wholeNumber(intervalMonths, 1);
    const days = wholeNumber(noticeDays, 0);
    const warnKm = wholeNumber(noticeKm, 0);

    if (!serviceType.trim()) return { error: 'serviceForm.errServiceType' };
    if (scope === 'vehicle' && !vehicleId) return { error: 'serviceForm.errVehicle' };
    if (km === INVALID || months === INVALID) return { error: 'serviceForm.errInterval' };
    // Answered here rather than let through as a 400, because the round trip
    // comes back in English and because "the form is half filled in" is not
    // a database error.
    if (km === null && months === null) return { error: 'serviceForm.errNoInterval' };
    if (days === INVALID || warnKm === INVALID) return { error: 'serviceForm.errNotice' };
    // Empty means "apply the default" on a NEW rule, and nothing at all on an
    // existing one: schema.sql has NOT NULL with a DEFAULT, so there is no
    // unset state to patch a row back into. Leaving it out would keep the
    // stored figure — which is defensible as data and dishonest as a form,
    // because the carrier cleared a box and the app would close having done
    // nothing. Sending the default instead would be worse: it would overwrite
    // a figure he chose with one he did not, and put a second copy of those
    // two numbers in this file to drift against schema.sql.
    if (isEdit && (days === null || warnKm === null)) {
      return { error: 'serviceForm.errNoticeOnEdit' };
    }

    const body = {
      service_type: serviceType.trim(),
      // null is the normal case and the useful one: the fleet default.
      vehicle_id: scope === 'vehicle' ? vehicleId : null,
      interval_km: km,
      interval_months: months,
    };
    // Only named when a human answered. schema.sql owns what "no answer"
    // means (14 days, 2 000 km) and repeating those two numbers in the
    // request would be a second copy to drift — invisibly, since both halves
    // would look right on their own while the carrier's amber window
    // depended on which of them wrote the row. The placeholders show the
    // defaults so nothing is applied behind his back.
    if (days !== null) body.notice_days = days;
    if (warnKm !== null) body.notice_km = warnKm;
    // A PATCH names the row it is changing. Everything else about the body is
    // identical to a create — the route accepts the same fields — so the two
    // paths cannot drift apart in what they consider a valid rule.
    if (isEdit) body.planId = plan.plan_id;
    return { body };
  };

  const recordBody = () => {
    const odometer = wholeNumber(odometerKm, 0);
    const cost = money(costEur);

    if (!vehicleId) return { error: 'serviceForm.errVehicle' };
    if (!serviceType.trim()) return { error: 'serviceForm.errServiceType' };
    if (!performedAt) return { error: 'serviceForm.errPerformedAt' };
    if (odometer === INVALID) return { error: 'serviceForm.errOdometer' };
    if (cost === INVALID) return { error: 'serviceForm.errCost' };

    return {
      body: {
        vehicle_id: vehicleId,
        service_type: serviceType.trim(),
        performed_at: performedAt,
        // null, never 0. The whole reason this file has a docstring.
        odometer_km: odometer,
        cost_eur: cost,
        workshop: workshop.trim() || null,
        notes: notes.trim() || null,
        // A PATCH names the row it changes. Everything else is identical to
        // a create, so the two paths cannot drift about what a valid record
        // is — the same rule planBody() follows one function up.
        ...(isEdit ? { recordId: record.record_id } : null),
      },
    };
  };

  const submit = () => {
    if (inFlight.current || save.isPending) return;
    const { body, error } = isPlan ? planBody() : recordBody();
    if (error) {
      setFormError(error);
      return;
    }
    // An edit that changed nothing is closed here rather than sent. The route
    // answers a body with no changed field with "Nėra ką keisti", which is
    // true and useless: somebody who opened a rule, read it and pressed save
    // has not made a mistake, and telling him he has is how a form teaches
    // people not to open things.
    if (isEdit && unchanged(body, isPlan ? plan : record)) {
      onClose?.();
      return;
    }
    setFormError(null);
    inFlight.current = true;
    save.mutate(body, {
      onSuccess: (row) => {
        onSaved?.(row);
        onClose?.();
      },
      // Cleared however it ended: a 409 has to leave the button usable, or
      // the carrier's only way out of a rejected save is to reload.
      onSettled: () => {
        inFlight.current = false;
      },
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      width="max-w-[620px]"
      title={
        /* Four titles for four states, not three. Until V-60 an edit said
           "Priežiūros taisyklė" whichever mode it was in, because a record
           could not be edited at all — and a correction dialog headed with
           the word for the RULE is the one thing that would make a carrier
           close it. The rule and the work done are the distinction this
           whole form is built around; the header is where he reads it. */
        isEdit
          ? (isPlan ? t('serviceForm.titleEditPlan') : t('serviceForm.titleEditRecord'))
          : (isPlan ? t('serviceForm.titlePlan') : t('serviceForm.titleRecord'))
      }
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={save.isPending}
          >
            {save.isPending ? t('serviceForm.saving') : t('common.saveBtn')}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-[11px] leading-snug text-dim">
          {isEdit
            ? (isPlan ? t('serviceForm.introEditPlan') : t('serviceForm.introEditRecord'))
            : (isPlan ? t('serviceForm.introPlan') : t('serviceForm.introRecord'))}
        </p>

        {isPlan ? (
          <Field label={t('serviceForm.scopeLabel')}>
            <select className="field" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="fleet">{t('serviceForm.scopeFleet')}</option>
              <option value="vehicle">{t('serviceForm.scopeVehicle')}</option>
            </select>
          </Field>
        ) : null}
        {isPlan ? <Hint>{t('serviceForm.scopeHint')}</Hint> : null}

        {/* Required on a record — a service happened to one truck or it did
            not happen. On a plan it appears only for an override, because
            the fleet default is the row most carriers want and asking them
            to pick a truck to get it would hide it. */}
        {!isPlan || scope === 'vehicle' ? (
          <Field label={t('transportas.vehicleLabel')}>
            <select
              className="field"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
            >
              <option value="">{t('transportas.pickVehicle')}</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                  {vehicle.license_plate}
                  {vehicle.make_model ? ` · ${vehicle.make_model}` : ''}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <Field label={t('serviceForm.serviceTypeLabel')}>
          <input
            className="field"
            value={serviceType}
            placeholder={t('serviceForm.serviceTypePh')}
            onChange={(e) => setServiceType(e.target.value)}
          />
        </Field>
        <Hint>{t('serviceForm.serviceTypeHint')}</Hint>

        {isPlan ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('serviceForm.intervalKmLabel')}>
                <input
                  className="field"
                  type="number"
                  min="1"
                  value={intervalKm}
                  placeholder="60000"
                  onChange={(e) => setIntervalKm(e.target.value)}
                />
              </Field>
              <Field label={t('serviceForm.intervalMonthsLabel')}>
                <input
                  className="field"
                  type="number"
                  min="1"
                  value={intervalMonths}
                  placeholder="12"
                  onChange={(e) => setIntervalMonths(e.target.value)}
                />
              </Field>
            </div>
            <Hint>{t('serviceForm.intervalHint')}</Hint>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('serviceForm.noticeDaysLabel')}>
                <input
                  className="field"
                  type="number"
                  min="0"
                  value={noticeDays}
                  // The schema default, shown rather than sent. An empty box
                  // stays out of the request entirely.
                  placeholder="14"
                  onChange={(e) => setNoticeDays(e.target.value)}
                />
              </Field>
              <Field label={t('serviceForm.noticeKmLabel')}>
                <input
                  className="field"
                  type="number"
                  min="0"
                  value={noticeKm}
                  placeholder="2000"
                  onChange={(e) => setNoticeKm(e.target.value)}
                />
              </Field>
            </div>
            <Hint>{t('serviceForm.noticeHint')}</Hint>
          </>
        ) : (
          <>
            <Field label={t('serviceForm.performedAtLabel')}>
              <input
                className="field"
                type="date"
                value={performedAt}
                onChange={(e) => setPerformedAt(e.target.value)}
              />
            </Field>

            <Field label={t('serviceForm.odometerLabel')}>
              <input
                className="field"
                type="number"
                min="0"
                value={odometerKm}
                onChange={(e) => setOdometerKm(e.target.value)}
              />
            </Field>
            {/* Beside the field, not in a tooltip: leaving this empty is the
                RIGHT answer when nobody wrote the reading down, and a
                carrier who does not know that types a guess. */}
            <Hint>{t('serviceForm.odometerHint')}</Hint>

            <Field label={t('serviceForm.costLabel')}>
              {/* Text, not number: the invoice says "148,50" and so does the
                  keyboard. A number input would refuse the comma. */}
              <input
                className="field"
                type="text"
                inputMode="decimal"
                value={costEur}
                onChange={(e) => setCostEur(e.target.value)}
              />
            </Field>
            <Hint>{t('serviceForm.costHint')}</Hint>

            <Field label={t('serviceForm.workshopLabel')}>
              <input
                className="field"
                value={workshop}
                onChange={(e) => setWorkshop(e.target.value)}
              />
            </Field>

            <Field label={t('serviceForm.notesLabel')}>
              <textarea
                className="field min-h-[72px]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>
          </>
        )}

        {/* What the form itself refused, in Lithuanian. */}
        {formError ? <ErrorState>{t(formError)}</ErrorState> : null}

        {/* What the server refused, in the server's own words. The 409s both
            routes can return name the row that is in the way — the fleet-wide
            plan, or this truck's service on that day — and each names a
            different next action. Replacing that with a generic "save failed"
            would throw away the only part of the answer worth reading. */}
        {save.isError ? <ErrorState error={save.error} /> : null}
      </div>
    </Modal>
  );
}

/** Same markup as DefectFormModal's, so the two forms in this folder look
 *  like one product. */
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">{label}</span>
      {children}
    </label>
  );
}

/** Deliberately outside <label>: these sentences are the reason a field can
 *  be left empty, and folding them into the label would put a paragraph into
 *  the field's accessible name. */
function Hint({ children }) {
  return <p className="-mt-1.5 text-[10.5px] leading-snug text-dim">{children}</p>;
}
